import type { CSSProperties, ReactNode } from 'react';
import { useState } from 'react';
import { ADVENT_MS, type AdventScene } from '../../lib/advent';
import { showDuration, type ShowId } from '../../lib/hourlyShow';

function Art({
  file,
  className,
  fallback,
  style,
}: {
  file: string;
  className?: string;
  fallback: ReactNode;
  style?: CSSProperties;
}) {
  const [failed, setFailed] = useState(false);
  if (failed) return fallback;
  return (
    <img
      className={className}
      style={style}
      src={`/emoji/${file}`}
      alt=""
      draggable={false}
      onError={() => setFailed(true)}
    />
  );
}

const ADVENT_ART: Record<number, string> = {
  1: 'star.webp',
  2: 'candy.png',
  3: 'bell.webp',
  4: 'stocking.webp',
  5: 'cookie.webp',
  6: 'guard.png',
  7: 'snowman.webp',
  8: 'ribbon.png',
  9: 'notes.webp',
  10: 'bulb.webp',
  11: 'cocoa.webp',
  12: 'gift.webp',
  13: 'angel.png',
  14: 'drum.webp',
  15: 'candle.png',
  16: 'herb.webp',
  17: 'orb.webp',
  19: 'elf.png',
  20: 'milk.png',
  21: 'bell.webp',
  22: 'glow-star.webp',
  23: 'deer.png',
  24: 'santa.png',
  25: 'tree.png',
};

const FLOWER_ART = ['blossom.webp', 'tulip.webp', 'flower.webp', 'hibiscus.png'];

function Cupid() {
  return (
    <svg viewBox="0 0 140 90" width="150" height="96" aria-hidden>
      <ellipse cx="34" cy="42" rx="22" ry="10" fill="#fbcfe8" transform="rotate(-24 34 42)" />
      <ellipse cx="78" cy="42" rx="22" ry="10" fill="#f9a8d4" transform="rotate(22 78 42)" />
      <circle cx="56" cy="46" r="16" fill="#fdba74" />
      <circle cx="51" cy="44" r="1.6" fill="#431407" />
      <circle cx="60" cy="44" r="1.6" fill="#431407" />
      <path d="M86 58 Q112 28 132 56" fill="none" stroke="#92400e" strokeWidth="4" />
      <path d="M124 48 L136 58 L122 64 Z" fill="#ef4444" />
      <path d="M48 62 Q56 70 66 60" fill="none" stroke="#9f1239" strokeWidth="2" />
    </svg>
  );
}

function Leprechaun() {
  return (
    <svg viewBox="0 0 80 110" width="96" height="132" aria-hidden>
      <path d="M18 28 L40 4 L62 28 Z" fill="#15803d" />
      <rect x="16" y="26" width="48" height="7" rx="2" fill="#166534" />
      <rect x="36" y="2" width="8" height="8" rx="1" fill="#facc15" />
      <circle cx="40" cy="40" r="14" fill="#fdba74" />
      <path d="M24 46 Q40 62 56 46 Q50 58 40 60 Q30 58 24 46" fill="#ea580c" />
      <rect x="26" y="58" width="28" height="28" rx="4" fill="#15803d" />
      <rect x="30" y="64" width="20" height="6" fill="#facc15" />
      <rect x="24" y="86" width="10" height="18" rx="3" fill="#14532d" />
      <rect x="46" y="86" width="10" height="18" rx="3" fill="#14532d" />
      <circle cx="29" cy="102" r="5" fill="#1c1917" />
      <circle cx="51" cy="102" r="5" fill="#1c1917" />
    </svg>
  );
}

function Mummy() {
  return (
    <div className="show-mummy" aria-hidden>
      <div className="show-mummy-head" />
      <div className="show-mummy-body" />
    </div>
  );
}

const FLOWER_FALLBACK = ['🌸', '🌷', '🌼', '🌺', '🌸', '🌷', '🌼', '🌺'];

export function ShowScene({ id }: { id: ShowId }) {
  const style = { animationDuration: `${showDuration(id)}ms` };
  return (
    <div className="show-scene" style={style} data-scene={id}>
      {id === 'blizzard' && <Blizzard />}
      {id === 'cupid' && <CupidShow />}
      {id === 'leprechaun' && <LeprechaunShow />}
      {id === 'rain' && <Rain />}
      {id === 'bloom' && <Bloom />}
      {id === 'beach' && <Beach />}
      {id === 'sun' && <Sun />}
      {id === 'campfire' && <Campfire />}
      {id === 'leaf-pile' && <LeafPile />}
      {id === 'monsters' && <Monsters />}
      {id === 'turkeys' && <Turkeys />}
      {id === 'sleigh' && <Sleigh />}
    </div>
  );
}

function Blizzard() {
  return (
    <>
      <div className="show-veil" />
      {Array.from({ length: 36 }, (_, i) => {
        const n = i + 1;
        const size = 16 + (n % 4) * 8;
        const flakeStyle = {
          left: `${(n * 27) % 100}%`,
          animationDuration: `${1.4 + (n % 5) * 0.35}s`,
          animationDelay: `${-((n * 0.2) % 2)}s`,
          fontSize: `${size}px`,
          width: `${size}px`,
          height: `${size}px`,
          '--drift': `${(n % 2 === 0 ? 1 : -1) * (10 + (n % 5) * 8)}px`,
        } as CSSProperties;
        return (
          <Art
            key={n}
            file="snowflake.png"
            className="show-snow"
            style={flakeStyle}
            fallback={
              <span className="show-snow" style={flakeStyle}>
                ❄
              </span>
            }
          />
        );
      })}
    </>
  );
}

function CupidShow() {
  return (
    <>
      <div className="show-flyer">
        <Art file="cupid.webp" className="show-hero" fallback={<Cupid />} />
      </div>
      {Array.from({ length: 8 }, (_, i) => (
        <Art
          key={i}
          file="heart.webp"
          className="show-heart"
          style={{
            left: `${12 + i * 10}%`,
            animationDelay: `${0.4 + i * 0.7}s`,
          }}
          fallback={
            <span
              className="show-heart"
              style={{
                left: `${12 + i * 10}%`,
                animationDelay: `${0.4 + i * 0.7}s`,
              }}
            >
              ♥
            </span>
          }
        />
      ))}
    </>
  );
}

function LeprechaunShow() {
  return (
    <>
      <div className="show-rainbow" />
      <div className="show-flyer" style={{ top: 'auto', bottom: '6%' }}>
        <Leprechaun />
      </div>
      {Array.from({ length: 4 }, (_, i) => (
        <Art
          key={`clover-${i}`}
          file="clover.webp"
          className="show-coins"
          style={{
            left: `${8 + i * 16}%`,
            animationDelay: `${i * 0.45}s`,
          }}
          fallback={null}
        />
      ))}
      {Array.from({ length: 6 }, (_, i) => (
        <Art
          key={`coin-${i}`}
          file="coin.webp"
          className="show-coins"
          style={{
            left: `${20 + ((i * 12) % 60)}%`,
            animationDelay: `${i * 0.35}s`,
          }}
          fallback={
            <span
              className="show-coins"
              style={{
                left: `${20 + ((i * 12) % 60)}%`,
                animationDelay: `${i * 0.35}s`,
              }}
            />
          }
        />
      ))}
    </>
  );
}

function Rain() {
  return (
    <>
      <div className="show-rain-front" />
      <Art file="rain-cloud.png" className="show-cloud" fallback={<div className="show-cloud" />} />
      {Array.from({ length: 18 }, (_, i) => {
        const style = {
          left: `${(i * 17) % 100}%`,
          animationDelay: `${(i % 8) * 0.08}s`,
          animationDuration: `${0.7 + (i % 4) * 0.12}s`,
        };
        return (
          <Art
            key={i}
            file="drop.webp"
            className="show-rain"
            style={style}
            fallback={<span className="show-rain" style={style} />}
          />
        );
      })}
    </>
  );
}

function Bloom() {
  return (
    <>
      {FLOWER_ART.concat(FLOWER_ART).map((file, i) => (
        <div
          key={i}
          className="show-flower"
          style={{ left: `${6 + i * 11}%`, animationDelay: `${i * 0.28}s` }}
        >
          <Art
            file={file}
            className="show-flower-bloom"
            fallback={<div className="show-flower-bloom">{FLOWER_FALLBACK[i]}</div>}
          />
          <div className="show-stem" />
        </div>
      ))}
    </>
  );
}

function Beach() {
  return (
    <>
      <div className="show-drop show-sand" />
      <Art
        file="chair.png"
        className="show-drop show-chair show-piece"
        fallback={<div className="show-drop show-chair" />}
      />
      <div className="show-drop show-cooler" />
      <Art
        file="umbrella.png"
        className="show-drop show-umbrella show-piece"
        fallback={<div className="show-drop show-umbrella" />}
      />
    </>
  );
}

function Sun() {
  return (
    <>
      <div className="show-sun">
        <Art file="sun.webp" className="show-sun-face" fallback={null} />
      </div>
      <div className="show-dry" />
    </>
  );
}

function Campfire() {
  return (
    <>
      <div className="show-drop show-log-a">
        <Art
          file="wood.png"
          className="show-log-img show-log-tilt-a"
          fallback={<div className="show-log show-log-tilt-a" />}
        />
      </div>
      <div className="show-drop show-log-b">
        <Art
          file="wood.png"
          className="show-log-img show-log-tilt-b"
          fallback={<div className="show-log show-log-tilt-b" />}
        />
      </div>
      <div className="show-drop show-log-c">
        <Art
          file="wood.png"
          className="show-log-img show-log-tilt-c"
          fallback={<div className="show-log show-log-tilt-c" />}
        />
      </div>
      <div className="show-match" />
      <div className="show-flames" aria-hidden>
        <Art
          file="fire.webp"
          className="show-flame-img"
          fallback={
            <>
              <span />
              <span />
              <span />
            </>
          }
        />
      </div>
    </>
  );
}

function LeafPile() {
  return (
    <>
      {Array.from({ length: 28 }, (_, i) => {
        const n = i + 1;
        const style = {
          left: `${(n * 19) % 96}%`,
          animationDelay: `${(n % 8) * 0.18}s`,
          '--drift': `${(n % 2 === 0 ? 1 : -1) * (12 + (n % 5) * 10)}px`,
          '--land': `${42 + (n % 6) * 7}vh`,
        } as CSSProperties;
        return (
          <Art
            key={n}
            file={n % 2 === 0 ? 'leaf.webp' : 'maple.webp'}
            className="show-leaf"
            style={style}
            fallback={
              <span className="show-leaf" style={style}>
                {n % 2 === 0 ? '🍂' : '🍁'}
              </span>
            }
          />
        );
      })}
    </>
  );
}

function Monsters() {
  return (
    <div className="show-parade">
      <Art file="wolf.webp" className="show-emoji" fallback={<span className="show-emoji">🐺</span>} />
      <Mummy />
      <Art file="vampire.png" className="show-emoji" fallback={<span className="show-emoji">🧛</span>} />
      <Art file="zombie.png" className="show-emoji" fallback={<span className="show-emoji">🧟</span>} />
      <Art file="ghost.webp" className="show-emoji show-float" fallback={<span className="show-emoji">👻</span>} />
      <Art file="skull.webp" className="show-emoji" fallback={<span className="show-emoji">💀</span>} />
    </div>
  );
}

function Turkeys() {
  return (
    <div className="show-parade">
      <Art file="turkey.png" className="show-emoji" fallback={<span className="show-emoji">🦃</span>} />
      <Art file="turkey.png" className="show-emoji" fallback={<span className="show-emoji">🦃</span>} />
      <Art file="turkey.png" className="show-emoji" fallback={<span className="show-emoji">🦃</span>} />
    </div>
  );
}

function Sleigh() {
  return (
    <div className="show-sleigh">
      <Art file="deer.png" className="show-emoji" fallback={<span className="show-emoji">🦌</span>} />
      <Art file="deer.png" className="show-emoji" fallback={<span className="show-emoji">🦌</span>} />
      <span className="show-sled">
        <Art file="santa.png" className="show-emoji" fallback={<span className="show-emoji">🎅</span>} />
      </span>
    </div>
  );
}

export function AdventSceneView({ scene }: { scene: AdventScene }) {
  return (
    <div
      className={`show-scene show-advent show-advent--${scene.motion}`}
      style={{ animationDuration: `${ADVENT_MS}ms` }}
      data-advent={scene.day}
    >
      {scene.day === 18 ? (
        <div className="show-sign">North Pole</div>
      ) : (
        <Art
          file={ADVENT_ART[scene.day] ?? ''}
          className="show-advent-glyph"
          fallback={<div className="show-advent-glyph">{scene.glyph}</div>}
        />
      )}
      <p className="show-advent-label">
        Day {scene.day} · {scene.label}
      </p>
    </div>
  );
}
